import React, { useEffect, useRef } from "react";
import { pickColor, hexToRgb, rgba } from "./internal/colorUtils";

export const key = "wave-interference";

const DEFAULT_LOW = "#0c1024";
const DEFAULT_HIGH = "#22d3ee";
const DEFAULT_SOURCE = "#fde68a";

// Two-source interference pattern. Each source emits a sinusoidal
// wavefront; at every pixel the local amplitude is the sum of cos(k*d - ωt)
// from both sources, where d is the distance to the source. We render
// into a low-resolution offscreen buffer (this is an O(W*H*sources)
// operation) and then upscale onto the visible canvas — the soft scaling
// that comes for free actually helps the bands feel organic.
const LOW_W = 220;
const LOW_H = 130;
const WAVE_NUMBER = 0.45;       // k — controls band spacing in low-res buffer pixels
const TIME_SPEED = 0.0035;      // ω scaled — radians per ms
const SOURCE_DRIFT_SPEED = 0.18; // px/frame the sources orbit around their home positions
const SOURCE_RADIUS_FRAC = 0.18; // each source orbits its home this far across the buffer

function lerp(a, b, t) { return a + (b - a) * t; }

export default function WaveInterference({ colors = [], style }) {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const offCtxRef = useRef(null);
  const imageDataRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_LOW, DEFAULT_HIGH, DEFAULT_SOURCE]);
  const startRef = useRef(0);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_LOW),
      pickColor(colors, 1, DEFAULT_HIGH),
      pickColor(colors, 2, DEFAULT_SOURCE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const ensureOffscreen = () => {
      // The offscreen buffer is fixed-size — independent of the visible
      // canvas — because the per-pixel wave math is the bottleneck.
      if (!offscreenRef.current) {
        const off = document.createElement("canvas");
        off.width = LOW_W;
        off.height = LOW_H;
        offscreenRef.current = off;
        offCtxRef.current = off.getContext("2d");
        imageDataRef.current = offCtxRef.current.createImageData(LOW_W, LOW_H);
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ensureOffscreen();
    };

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const t = ts - startRef.current;
      const offCtx = offCtxRef.current;
      const imgData = imageDataRef.current;
      if (!offCtx || !imgData) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const [low, high, source] = colorsRef.current;
      const lowRgb = hexToRgb(low) || hexToRgb(DEFAULT_LOW);
      const highRgb = hexToRgb(high) || hexToRgb(DEFAULT_HIGH);
      const srcRgb = hexToRgb(source) || hexToRgb(DEFAULT_SOURCE);

      // Two sources orbit their home positions on the left and right
      // halves of the buffer. The slow circular drift makes the
      // interference bands rotate and pulse continuously.
      const homeLX = LOW_W * 0.32;
      const homeRX = LOW_W * 0.68;
      const homeY = LOW_H * 0.5;
      const orbR = LOW_W * SOURCE_RADIUS_FRAC * 0.4;
      const phase = t * SOURCE_DRIFT_SPEED * 0.001;
      const s1x = homeLX + Math.cos(phase) * orbR;
      const s1y = homeY + Math.sin(phase * 1.3) * orbR;
      const s2x = homeRX + Math.cos(phase * 0.9 + Math.PI) * orbR;
      const s2y = homeY + Math.sin(phase * 1.1 + 0.7) * orbR;

      const wt = t * TIME_SPEED;
      const data = imgData.data;
      let p = 0;
      for (let y = 0; y < LOW_H; y += 1) {
        for (let x = 0; x < LOW_W; x += 1) {
          const dx1 = x - s1x;
          const dy1 = y - s1y;
          const d1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
          const dx2 = x - s2x;
          const dy2 = y - s2y;
          const d2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

          // Sum of two sinusoids → range [-2, 2]. Normalize to [0, 1].
          const a = Math.cos(WAVE_NUMBER * d1 - wt) + Math.cos(WAVE_NUMBER * d2 - wt);
          const norm = (a + 2) * 0.25;

          // Highlight zones near each source by additively blending the
          // source color where the wavefront is strongest. Strength
          // falls off with 1/d so it doesn't drown the bands.
          const srcBoost = Math.min(1, 6 / (d1 + 6)) * 0.55 + Math.min(1, 6 / (d2 + 6)) * 0.55;

          const r = Math.min(255, lerp(lowRgb.r, highRgb.r, norm) + srcRgb.r * srcBoost * 0.25);
          const g = Math.min(255, lerp(lowRgb.g, highRgb.g, norm) + srcRgb.g * srcBoost * 0.25);
          const b = Math.min(255, lerp(lowRgb.b, highRgb.b, norm) + srcRgb.b * srcBoost * 0.25);

          data[p] = r;
          data[p + 1] = g;
          data[p + 2] = b;
          data[p + 3] = 255;
          p += 4;
        }
      }
      offCtx.putImageData(imgData, 0, 0);

      // Upscale the buffer over the entire visible canvas.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(offscreenRef.current, 0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Crisp source markers in canvas-space (above the upscaled buffer)
      // so they stay sharp regardless of the buffer resolution.
      const sx1 = (s1x / LOW_W) * width;
      const sy1 = (s1y / LOW_H) * height;
      const sx2 = (s2x / LOW_W) * width;
      const sy2 = (s2y / LOW_H) * height;
      for (const [sx, sy] of [[sx1, sy1], [sx2, sy2]]) {
        const haloR = 14;
        const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, haloR);
        halo.addColorStop(0, rgba(source, 0.85));
        halo.addColorStop(1, rgba(source, 0));
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = rgba(source, 1);
        ctx.beginPath();
        ctx.arc(sx, sy, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
